import { type Attraction } from '@/config/objects/attractions';
import {
  Alert,
  AlertDescription,
  Badge,
  Button,
  ConfirmDialog,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Input,
  toast,
  useApiClient,
  useApp,
} from '@startupquickstart/frontend';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  AdvancedMarker,
  APIProvider,
  Map,
  type MapMouseEvent,
} from '@vis.gl/react-google-maps';
import {
  AlertTriangle,
  BadgeCheck,
  Compass,
  MapPin,
  Maximize2,
  RotateCcw,
  Save,
  Search,
  ShieldAlert,
  X,
} from 'lucide-react';
import { useCallback, useEffect, useId, useMemo, useState } from 'react';

interface CoordinatesSectionProps {
  attraction: Attraction;
  /**
   * Layout mode for the panel.
   *
   * - `'stacked'` (default): map on top, lat/lng readout + actions below.
   *   Includes an Expand button on the inline map that opens a side-by-side
   *   modal. Use this on the narrow sidebar card on the attraction page.
   * - `'wide'`: map on the left filling the available height, with the
   *   readout, warning banner, verified-address details, and actions
   *   stacked on the right. No Expand button (there's no smaller view to
   *   escape). No internal modal. Use this when CoordinatesSection is
   *   already mounted inside a large container (e.g. the Health tab's
   *   verify dialog).
   */
  layout?: 'stacked' | 'wide';
  /**
   * Optional close handler shown as a "Close" button in the action footer.
   * Use this when CoordinatesSection is mounted inside a parent-owned
   * modal (e.g. the Health verify dialog) so the user can dismiss the
   * dialog without hunting for the corner X. In stacked mode, the
   * built-in Edit modal supplies its own close action automatically.
   */
  onClose?: () => void;
}

interface VerifiedAddressComponents {
  streetAddress?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
}

interface VerifiedAddress {
  latitude: number;
  longitude: number;
  formattedAddress: string;
  components: VerifiedAddressComponents;
  partialMatch: boolean;
  placeId: string;
}

interface VerifyResponse {
  success: boolean;
  data: VerifiedAddress;
}

interface UpdatePayload {
  latitude?: number;
  longitude?: number;
  streetAddress?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
  __verifiedBySource?: string;
  __verifiedSnapshot?: VerifiedAddress;
}

const VERIFICATION_SOURCE = 'google_geocoding';
const MANUAL_VERIFICATION_SOURCE = 'manual';
const DEFAULT_CENTER = { lat: 39.8283, lng: -98.5795 };
const DEFAULT_ZOOM = 4;
const PIN_ZOOM = 15;

const toNumber = (value: number | string | null | undefined): number | undefined => {
  if (value === null || value === undefined || value === '') return undefined;
  const n = typeof value === 'string' ? parseFloat(value) : value;
  return Number.isFinite(n) ? n : undefined;
};

interface InteractiveMapProps {
  coordinate: { lat: number; lng: number } | null;
  onChange: (coord: { lat: number; lng: number }) => void;
  className?: string;
  /**
   * Aspect ratio for the map's outer container. Width is taken from the
   * parent; height is derived. Tailwind's `aspect-*` classes are best — the
   * map then scales with the window/card width.
   *
   * @default "aspect-video" (16:9)
   */
  aspectClass?: string;
  showExpandButton?: boolean;
  onExpand?: () => void;
}

/**
 * Interactive Google Map with a single draggable + click-to-place pin.
 * Used both inline (compact) and inside the expand-to-modal view.
 */
function InteractiveMap({
  coordinate,
  onChange,
  className,
  aspectClass = 'aspect-video',
  showExpandButton = false,
  onExpand,
}: InteractiveMapProps) {
  const mapId = useId();
  const initialCenter = useMemo(
    () => coordinate ?? DEFAULT_CENTER,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  const initialZoom = useMemo(
    () => (coordinate ? PIN_ZOOM : DEFAULT_ZOOM),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  // Click placement is only used to drop the FIRST pin on a record that
  // doesn't have coordinates yet. Once a pin exists, the user moves it by
  // dragging — clicking just pans/positions the map. This avoids the
  // common footgun of accidentally relocating a verified pin while
  // looking around the surrounding area.
  const handleMapClick = useCallback(
    (e: MapMouseEvent) => {
      if (coordinate) return;
      const latLng = e.detail.latLng;
      if (!latLng) return;
      onChange({ lat: latLng.lat, lng: latLng.lng });
    },
    [coordinate, onChange],
  );

  // The @vis.gl AdvancedMarker forwards the underlying google.maps.MouseEvent
  // through `onDragEnd`. Avoid referencing `google.maps.*` directly so this
  // file doesn't depend on `@types/google.maps` being in the project's
  // tsconfig.types — we just narrow the shape we actually use.
  const handleDragEnd = useCallback(
    (e: { latLng: { lat: () => number; lng: () => number } | null }) => {
      const latLng = e.latLng;
      if (!latLng) return;
      onChange({ lat: latLng.lat(), lng: latLng.lng() });
    },
    [onChange],
  );

  return (
    <div
      className={`relative w-full overflow-hidden rounded-lg border ${aspectClass} ${className ?? ''}`}
    >
      <Map
        mapId={mapId}
        defaultCenter={initialCenter}
        defaultZoom={initialZoom}
        center={coordinate ?? undefined}
        gestureHandling="cooperative"
        disableDefaultUI={false}
        zoomControl
        mapTypeControl={false}
        streetViewControl={false}
        fullscreenControl={false}
        onClick={handleMapClick}
        className="h-full w-full"
      >
        {coordinate && (
          <AdvancedMarker position={coordinate} draggable onDragEnd={handleDragEnd} />
        )}
      </Map>

      {!coordinate && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <div className="rounded-md border bg-background/90 px-3 py-1.5 text-xs font-medium text-muted-foreground shadow-sm backdrop-blur">
            Click the map to place a pin
          </div>
        </div>
      )}

      {coordinate && (
        <div className="pointer-events-none absolute bottom-2 left-2 rounded-md border bg-background/90 px-2 py-1 text-[11px] font-medium text-muted-foreground shadow-sm backdrop-blur">
          Drag the pin to move it
        </div>
      )}

      {showExpandButton && onExpand && (
        <button
          type="button"
          onClick={onExpand}
          aria-label="Expand map"
          className="absolute right-2 top-2 inline-flex h-8 w-8 items-center justify-center rounded-md border bg-background/95 text-foreground shadow-sm backdrop-blur transition hover:bg-background"
        >
          <Maximize2 className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}

/**
 * Coordinates editor for the attraction overview tab. Always rendered:
 *   - With a Verified badge if the saved address+coords have been confirmed.
 *   - With an amber warning when no coordinates exist yet.
 *   - With a draggable / click-to-place interactive map (expandable to a
 *     full-screen modal).
 *   - With a "Use Google Places" button that geocodes from the saved
 *     (or proposed) address fields server-side and offers to backfill any
 *     missing street / postal / country data.
 *
 * Saving with the verify-result-still-pinned commits the verification stamp
 * and the snapshot of the geocoder response. Saving after any manual drag
 * commits the new coordinates without verification (and clears any prior
 * verification on the server side via the address-hash check).
 */
export function CoordinatesSection({
  attraction,
  layout = 'stacked',
  onClose,
}: CoordinatesSectionProps) {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const { config } = useApp();
  const googleMapsApiKey = config?.googleMapsApiKey;

  const savedLat = toNumber(attraction.latitude);
  const savedLng = toNumber(attraction.longitude);
  const savedCoordinate = useMemo(
    () => (savedLat !== undefined && savedLng !== undefined ? { lat: savedLat, lng: savedLng } : null),
    [savedLat, savedLng],
  );

  const [pendingCoordinate, setPendingCoordinate] = useState<{ lat: number; lng: number } | null>(
    savedCoordinate,
  );
  const [verifyResult, setVerifyResult] = useState<VerifiedAddress | null>(null);
  const [applyAddressFields, setApplyAddressFields] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  // What kind of verification stamp the user has armed for the next save.
  // - 'external': they ran the geocoder (Verify) and got a result. The
  //   stamp persists even if they tweak the pin afterward — the address
  //   is the verification anchor; the pin position is a fine-tune.
  // - 'manual': they explicitly clicked Mark as verified. No geocoder
  //   round-trip, no snapshot — staff is asserting the saved record is
  //   correct based on their own knowledge.
  // - null: regular save, no verification stamp.
  const [pendingVerification, setPendingVerification] = useState<
    'external' | 'manual' | null
  >(null);
  // True when the user has clicked Save while the pending coords differ
  // from the verifyResult (i.e. they ran Verify, then nudged the pin).
  // We pause the actual save and surface a ConfirmDialog so the user
  // explicitly acknowledges they are committing a manual override.
  const [confirmManualOverride, setConfirmManualOverride] = useState(false);
  // String-backed inputs for the lat / lng override fields. Stored as text
  // (not numbers) so the user can type partial values like "42." or "-"
  // without us snapping the display back. We only push a parsed value into
  // `pendingCoordinate` when both inputs round-trip to finite numbers in
  // a sensible range.
  const [latText, setLatText] = useState(
    pendingCoordinate ? pendingCoordinate.lat.toFixed(6) : '',
  );
  const [lngText, setLngText] = useState(
    pendingCoordinate ? pendingCoordinate.lng.toFixed(6) : '',
  );

  // Reset local state whenever the upstream record changes (e.g. after refetch).
  useEffect(() => {
    const next =
      savedLat !== undefined && savedLng !== undefined ? { lat: savedLat, lng: savedLng } : null;
    setPendingCoordinate(next);
    setVerifyResult(null);
    setApplyAddressFields(false);
    setPendingVerification(null);
    setLatText(next ? next.lat.toFixed(6) : '');
    setLngText(next ? next.lng.toFixed(6) : '');
  }, [savedLat, savedLng]);

  // Whenever `pendingCoordinate` changes from outside the inputs (drag,
  // Verify result, click-to-place), reflect that in the input text so the
  // two stay in sync. Skip the update if the parsed values already match
  // — otherwise typing a partial like "42.3" would get snapped back.
  useEffect(() => {
    if (!pendingCoordinate) {
      if (latText !== '') setLatText('');
      if (lngText !== '') setLngText('');
      return;
    }
    const parsedLat = parseFloat(latText);
    const parsedLng = parseFloat(lngText);
    if (parsedLat !== pendingCoordinate.lat || Number.isNaN(parsedLat)) {
      setLatText(pendingCoordinate.lat.toFixed(6));
    }
    if (parsedLng !== pendingCoordinate.lng || Number.isNaN(parsedLng)) {
      setLngText(pendingCoordinate.lng.toFixed(6));
    }
    // We intentionally don't include latText/lngText — this effect runs
    // when the upstream coordinate changes, not when the user is typing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingCoordinate]);

  // Commit a typed lat/lng pair into `pendingCoordinate` when both
  // values parse to finite numbers in a valid earth-coordinate range.
  // Out-of-range / NaN values just update the input text and leave the
  // pin where it was — same as a noop drag.
  const commitTypedCoordinate = (latStr: string, lngStr: string) => {
    const lat = parseFloat(latStr);
    const lng = parseFloat(lngStr);
    if (
      Number.isFinite(lat) &&
      Number.isFinite(lng) &&
      lat >= -90 &&
      lat <= 90 &&
      lng >= -180 &&
      lng <= 180
    ) {
      setPendingCoordinate({ lat, lng });
    }
  };

  const handleLatChange = (next: string) => {
    setLatText(next);
    commitTypedCoordinate(next, lngText);
  };

  const handleLngChange = (next: string) => {
    setLngText(next);
    commitTypedCoordinate(latText, next);
  };

  const verifyMutation = useMutation<VerifiedAddress, Error, void>({
    mutationFn: async () => {
      const response = await apiClient.post<VerifyResponse>(
        `/attractions/${attraction.id}/verify-address`,
        {},
      );
      return response.data;
    },
  });

  const saveMutation = useMutation<unknown, Error, UpdatePayload>({
    mutationFn: (payload) => apiClient.put(`/attractions/${attraction.id}`, payload),
    onSuccess: () => {
      // Refresh every cache that surfaces this attraction so the new
      // verified flag propagates everywhere — the attraction detail
      // page hero, the Health > Location lists for tours and greater
      // areas, the regular paginated attractions list, and the
      // sortable tour-attractions list. Partial-key invalidation
      // matches any cache entry whose key starts with the prefix.
      queryClient.invalidateQueries({ queryKey: ['Attraction', attraction.id] });
      queryClient.invalidateQueries({ queryKey: ['attractions'] });
      queryClient.invalidateQueries({ queryKey: ['TourAttractionOrder'] });
      queryClient.invalidateQueries({ queryKey: ['GreaterAreaAttractions'] });
    },
  });

  const isDirty = useMemo(() => {
    if (!pendingCoordinate && !savedCoordinate) return false;
    if (!pendingCoordinate || !savedCoordinate) return true;
    return (
      pendingCoordinate.lat !== savedCoordinate.lat ||
      pendingCoordinate.lng !== savedCoordinate.lng
    );
  }, [pendingCoordinate, savedCoordinate]);

  // Three header-badge states so reviewers always know where the record
  // stands. Mutually exclusive — pending takes priority over saved.
  //
  // - showVerifiedBadge: server says verified AND no local edits / pending
  //   stamp. Steady emerald pill.
  // - showPendingVerifyBadge: a stamp has been armed but not saved yet.
  //   Sky-blue "Will verify on save" pill.
  // - showUnverifiedBadge: the saved record is unverified and the user
  //   hasn't armed a stamp. Amber pill so it's obvious this still needs
  //   attention. (Without this, the header was empty and reviewers
  //   couldn't tell at a glance that the record needed verification.)
  const showVerifiedBadge =
    !!attraction.isAddressVerified && !isDirty && !pendingVerification;
  const showPendingVerifyBadge = !!pendingVerification;
  const showUnverifiedBadge =
    !showVerifiedBadge && !showPendingVerifyBadge;
  const showWarning = !savedCoordinate;

  const verifiedDifferences = useMemo(() => {
    if (!verifyResult) return [];
    const c = verifyResult.components;
    const diffs: { field: keyof UpdatePayload; current: string | undefined; verified: string }[] = [];
    if (c.streetAddress && c.streetAddress !== attraction.streetAddress) {
      diffs.push({
        field: 'streetAddress',
        current: attraction.streetAddress,
        verified: c.streetAddress,
      });
    }
    if (c.city && c.city !== attraction.city) {
      diffs.push({ field: 'city', current: attraction.city, verified: c.city });
    }
    if (c.state && c.state !== attraction.state) {
      diffs.push({ field: 'state', current: attraction.state, verified: c.state });
    }
    if (c.postalCode && c.postalCode !== attraction.postalCode) {
      diffs.push({ field: 'postalCode', current: attraction.postalCode, verified: c.postalCode });
    }
    if (c.country && c.country !== attraction.country) {
      diffs.push({ field: 'country', current: attraction.country, verified: c.country });
    }
    return diffs;
  }, [verifyResult, attraction]);

  const handleVerify = async () => {
    try {
      const result = await verifyMutation.mutateAsync();
      setVerifyResult(result);
      setPendingCoordinate({ lat: result.latitude, lng: result.longitude });
      setApplyAddressFields(false);
      // Arm the external-verification stamp. The address is the
      // verification anchor — if the user fine-tunes the pin afterward
      // (e.g. moves it from the geocoded center of Boston Common to the
      // Freedom Trail start), the stamp still applies on save.
      setPendingVerification('external');
    } catch (error) {
      toast.error(
        error instanceof Error
          ? `Could not verify address: ${error.message}`
          : 'Could not verify this address. Try filling in the city and state.',
      );
    }
  };

  // Stamp the saved record as verified without calling the geocoder. Used
  // when staff knows from their own research that the saved address +
  // pin position are correct (e.g. a one-off attraction the geocoder
  // can't resolve cleanly).
  const handleManualVerify = () => {
    setVerifyResult(null);
    setApplyAddressFields(false);
    setPendingVerification('manual');
    toast.success('Marked as verified — Save to apply.');
  };

  const handleReset = () => {
    setPendingCoordinate(savedCoordinate);
    setVerifyResult(null);
    setApplyAddressFields(false);
    setPendingVerification(null);
  };

  // True when the user ran Verify, the geocoder returned, and the
  // pending coordinate has since been nudged (drag, type, click). The
  // address-level verification still holds — we just want the user to
  // explicitly acknowledge that the pin is a manual override.
  const isManualOverrideOfVerified =
    pendingVerification === 'external' &&
    !!verifyResult &&
    !!pendingCoordinate &&
    (pendingCoordinate.lat !== verifyResult.latitude ||
      pendingCoordinate.lng !== verifyResult.longitude);

  // Run the actual save round-trip. Called either directly (no
  // confirmation needed) or from the ConfirmDialog after the user
  // acknowledges a manual override.
  const performSave = async () => {
    if (!pendingCoordinate) return;

    const payload: UpdatePayload = {
      latitude: pendingCoordinate.lat,
      longitude: pendingCoordinate.lng,
    };

    if (pendingVerification === 'external' && verifyResult) {
      // Verify-then-tweak: even if the user nudged the pin after the
      // geocoder returned, the address-level verification still holds.
      payload.__verifiedBySource = VERIFICATION_SOURCE;
      payload.__verifiedSnapshot = verifyResult;

      if (applyAddressFields) {
        const c = verifyResult.components;
        if (c.streetAddress) payload.streetAddress = c.streetAddress;
        if (c.city) payload.city = c.city;
        if (c.state) payload.state = c.state;
        if (c.postalCode) payload.postalCode = c.postalCode;
        if (c.country) payload.country = c.country;
      }
    } else if (pendingVerification === 'manual') {
      // Manual stamp — staff asserting the data is correct. No
      // geocoder snapshot to attach.
      payload.__verifiedBySource = MANUAL_VERIFICATION_SOURCE;
    }

    try {
      await saveMutation.mutateAsync(payload);
      toast.success(
        pendingVerification
          ? 'Coordinates saved and marked as verified'
          : 'Coordinates saved',
      );
      setVerifyResult(null);
      setApplyAddressFields(false);
      setPendingVerification(null);
      setIsExpanded(false);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to save coordinates');
    }
  };

  const handleSave = async () => {
    if (!pendingCoordinate) return;
    // If the user ran Verify and then moved the pin, pause and ask them
    // to acknowledge the manual override before stamping verified.
    if (isManualOverrideOfVerified) {
      setConfirmManualOverride(true);
      return;
    }
    await performSave();
  };

  // Save needs a pin AND either a coordinate change OR an armed
  // verification stamp (manual-verify-without-tweaking is a valid save
  // path — same coords, just adds the verification flag).
  const isSaveDisabled =
    !pendingCoordinate ||
    saveMutation.isPending ||
    (!isDirty && !pendingVerification);

  // Already-verified rows don't need the Mark as verified button — until
  // the user makes a change. After a tweak, re-confirm makes sense.
  const showManualVerify =
    !pendingVerification && (!attraction.isAddressVerified || isDirty);

  // Action footer. `closeHandler` is supplied by whichever container is
  // hosting us (inline Edit modal, Health verify dialog, etc.); when
  // omitted, no Close button renders — the inline sidebar variant has
  // nothing to close so showing one would just confuse.
  const renderActions = (closeHandler?: () => void) => (
    <div className="flex flex-wrap items-center gap-2">
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={handleVerify}
        disabled={verifyMutation.isPending}
        title="Geocode the saved address through Google and pre-fill the pin."
      >
        <Search className="h-4 w-4" />
        {verifyMutation.isPending ? 'Verifying…' : 'Verify'}
      </Button>
      {showManualVerify && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleManualVerify}
          title="Mark the saved address + pin as correct without calling the geocoder."
        >
          <BadgeCheck className="h-4 w-4" />
          Mark as verified
        </Button>
      )}
      <span className="ml-auto" />
      {(isDirty || pendingVerification) && (
        <Button type="button" variant="outline" size="sm" onClick={handleReset}>
          <RotateCcw className="h-4 w-4" />
          Reset
        </Button>
      )}
      <Button type="button" size="sm" onClick={handleSave} disabled={isSaveDisabled}>
        <Save className="h-4 w-4" />
        Save
      </Button>
      {closeHandler && (
        <Button type="button" variant="outline" size="sm" onClick={closeHandler}>
          <X className="h-4 w-4" />
          Close
        </Button>
      )}
    </div>
  );

  const renderMapBody = (variant: 'inline' | 'fullscreen' | 'fill') => {
    // 'inline' / 'fullscreen' use aspect-ratio sizing — width comes from the
    // parent and height is derived. 'fill' takes 100% of the parent height
    // and is used when the map sits next to a side panel that already
    // controls the layout (the wide / side-by-side mode).
    const sizingClass =
      variant === 'fill'
        ? 'h-full'
        : variant === 'fullscreen'
          ? 'aspect-video'
          : 'aspect-[16/9]';

    if (!googleMapsApiKey) {
      return (
        <div
          className={`flex w-full items-center justify-center rounded-lg border bg-muted/30 text-sm text-muted-foreground ${sizingClass}`}
        >
          Map unavailable — Google Maps API key not configured
        </div>
      );
    }
    return (
      <APIProvider apiKey={googleMapsApiKey}>
        <InteractiveMap
          coordinate={pendingCoordinate}
          onChange={setPendingCoordinate}
          aspectClass={sizingClass}
          showExpandButton={variant === 'inline'}
          onExpand={() => setIsExpanded(true)}
        />
      </APIProvider>
    );
  };

  const renderReadout = () => (
    <div className="space-y-2 text-sm">
      {pendingCoordinate ? (
        <div>
          <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Lat / Lng
          </div>
          <div className="font-mono">
            {pendingCoordinate.lat.toFixed(6)}, {pendingCoordinate.lng.toFixed(6)}
          </div>
        </div>
      ) : (
        <div className="text-muted-foreground">No coordinates set</div>
      )}

      {verifyResult && (
        <div>
          <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Verified address
          </div>
          <div>{verifyResult.formattedAddress}</div>
          {verifiedDifferences.length > 0 && (
            <label className="mt-2 flex cursor-pointer items-center gap-2 text-xs">
              <input
                type="checkbox"
                checked={applyAddressFields}
                onChange={(e) => setApplyAddressFields(e.target.checked)}
                className="h-3.5 w-3.5"
              />
              Use these address fields too ({verifiedDifferences.length} change
              {verifiedDifferences.length === 1 ? '' : 's'})
            </label>
          )}
        </div>
      )}
    </div>
  );

  // Header bar shown above the panel — title + Verified / Pending / Unverified.
  const renderHeader = () => (
    <div className="flex items-center justify-between gap-2">
      <h3 className="text-sm font-semibold tracking-tight">Address &amp; Location</h3>
      {showVerifiedBadge && (
        <Badge variant="outline" className="gap-1 border-emerald-200 bg-emerald-50 text-emerald-700">
          <BadgeCheck className="h-3 w-3" />
          Verified
        </Badge>
      )}
      {showPendingVerifyBadge && (
        <Badge variant="outline" className="gap-1 border-sky-200 bg-sky-50 text-sky-700">
          <BadgeCheck className="h-3 w-3" />
          Will be verified on save
        </Badge>
      )}
      {showUnverifiedBadge && (
        <Badge variant="outline" className="gap-1 border-amber-200 bg-amber-50 text-amber-800">
          <ShieldAlert className="h-3 w-3" />
          Unverified
        </Badge>
      )}
    </div>
  );

  // Amber warning shown when the saved record has no coordinates at all.
  const renderWarning = () =>
    showWarning ? (
      <Alert variant="default" className="border-amber-200 bg-amber-50 text-amber-800">
        <AlertTriangle className="h-4 w-4 text-amber-700" />
        <AlertDescription className="text-amber-800">
          This attraction has no coordinates yet. Tap Verify to find them from the address, or click
          the map to place a pin manually.
        </AlertDescription>
      </Alert>
    ) : null;

  // Side-by-side layout: full-height map on the left, info / actions on the
  // right. Caller is responsible for giving us a parent with a defined
  // height (e.g. flex-1 inside a flex column).
  //
  // The right column is a proper card — stat-style sections with eyebrow
  // labels, generous whitespace between groups, and a pinned action
  // footer so Save / Verify sit predictably at the bottom regardless of
  // how much content is in the body.
  // `closeHandler` flows through to the action footer so the Close
  // button knows which container to dismiss. Different host modals
  // need different close behavior so we let the call site pass it in.
  const renderSideBySide = (closeHandler?: () => void) => (
    <div className="grid h-full min-h-0 grid-cols-1 gap-3 lg:grid-cols-[minmax(0,2fr)_minmax(320px,1fr)]">
      <div className="min-h-0">{renderMapBody('fill')}</div>

      <aside className="flex min-h-0 flex-col overflow-hidden rounded-xl border bg-card shadow-sm">
        <header className="flex items-start justify-between gap-2 border-b bg-muted/30 px-4 py-3">
          <div className="space-y-0.5">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              Verification
            </p>
            <h3 className="flex items-center gap-2 text-base font-semibold tracking-tight">
              <MapPin className="h-4 w-4 text-muted-foreground" />
              Address &amp; Location
            </h3>
          </div>
          {showVerifiedBadge && (
            <Badge
              variant="outline"
              className="shrink-0 gap-1 border-emerald-200 bg-emerald-50 text-emerald-700"
            >
              <BadgeCheck className="h-3 w-3" />
              Verified
            </Badge>
          )}
          {showPendingVerifyBadge && (
            <Badge
              variant="outline"
              className="shrink-0 gap-1 border-sky-200 bg-sky-50 text-sky-700"
            >
              <BadgeCheck className="h-3 w-3" />
              Will verify on save
            </Badge>
          )}
          {showUnverifiedBadge && (
            <Badge
              variant="outline"
              className="shrink-0 gap-1 border-amber-200 bg-amber-50 text-amber-800"
            >
              <ShieldAlert className="h-3 w-3" />
              Unverified
            </Badge>
          )}
        </header>

        <div className="flex-1 space-y-4 overflow-y-auto px-4 py-4">
          {/* Saved address — shown so the reviewer can read what's on the
              record without scrolling away from the verify flow. Falls
              back to a placeholder when nothing is set. */}
          <section className="space-y-1.5">
            <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              <MapPin className="h-3 w-3" />
              Saved address
            </div>
            {(() => {
              const street = [attraction.streetAddress, attraction.streetAddress2]
                .filter((s): s is string => !!s && s.trim().length > 0)
                .join(' ');
              const cityState = [attraction.city, attraction.state]
                .filter((s): s is string => !!s && s.trim().length > 0)
                .join(', ');
              const postal = attraction.postalCode?.trim();
              const country = attraction.country?.trim();
              const lines = [
                street,
                [cityState, postal].filter(Boolean).join(' '),
                country,
              ].filter((l) => l && l.length > 0);
              if (lines.length === 0) {
                return (
                  <p className="text-sm italic text-muted-foreground">
                    No address on this attraction yet.
                  </p>
                );
              }
              return (
                <address className="not-italic text-sm leading-snug text-foreground">
                  {lines.map((line, idx) => (
                    <div key={idx}>{line}</div>
                  ))}
                </address>
              );
            })()}
          </section>

          <section className="space-y-2">
            <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              <Compass className="h-3 w-3" />
              Coordinates
            </div>
            <div className="grid grid-cols-2 gap-2">
              <label className="space-y-1">
                <span className="text-[11px] font-medium text-muted-foreground">Latitude</span>
                <Input
                  type="number"
                  step="any"
                  inputMode="decimal"
                  value={latText}
                  onChange={(e) => handleLatChange(e.target.value)}
                  placeholder="—"
                  className="font-mono text-sm tabular-nums"
                />
              </label>
              <label className="space-y-1">
                <span className="text-[11px] font-medium text-muted-foreground">Longitude</span>
                <Input
                  type="number"
                  step="any"
                  inputMode="decimal"
                  value={lngText}
                  onChange={(e) => handleLngChange(e.target.value)}
                  placeholder="—"
                  className="font-mono text-sm tabular-nums"
                />
              </label>
            </div>
            <p className="text-[11px] leading-snug text-muted-foreground">
              Drag the pin on the map, or override here directly — useful when
              the geocoded center is off (e.g. parks like Boston Common — pin
              the Freedom Trail start instead of the middle of the green).
            </p>
          </section>

          {verifyResult && (
            <section className="space-y-1.5">
              <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                Verified address
              </div>
              <p className="text-sm leading-snug text-foreground">
                {verifyResult.formattedAddress}
              </p>
              {verifiedDifferences.length > 0 && (
                <label className="mt-1 flex cursor-pointer items-start gap-2 rounded-md border bg-muted/40 p-2.5 text-xs">
                  <input
                    type="checkbox"
                    checked={applyAddressFields}
                    onChange={(e) => setApplyAddressFields(e.target.checked)}
                    className="mt-0.5 h-3.5 w-3.5"
                  />
                  <span>
                    Use these address fields too
                    <span className="ml-1 text-muted-foreground">
                      ({verifiedDifferences.length} change
                      {verifiedDifferences.length === 1 ? '' : 's'})
                    </span>
                  </span>
                </label>
              )}
            </section>
          )}

          {renderWarning()}
        </div>

        <footer className="border-t bg-muted/30 px-4 py-3">{renderActions(closeHandler)}</footer>
      </aside>
    </div>
  );

  // The override-prompt dialog is rendered alongside whichever layout
  // we picked, so the prompt fires in both the inline Edit modal and
  // the Health verify dialog.
  const overridePrompt = (
    <ConfirmDialog
      open={confirmManualOverride}
      onOpenChange={setConfirmManualOverride}
      title="Save with manual pin override?"
      description={
        verifyResult
          ? `Google placed the pin at ${verifyResult.formattedAddress}. You moved it to ${
              pendingCoordinate
                ? `${pendingCoordinate.lat.toFixed(6)}, ${pendingCoordinate.lng.toFixed(6)}`
                : 'a different location'
            }. Saving will keep the address marked as verified but record the new position as a manual override.`
          : 'You moved the pin away from the geocoded result. Save anyway and keep the verified status?'
      }
      confirmText="Yes, save manual override"
      cancelText="Keep editing"
      loading={saveMutation.isPending}
      onConfirm={performSave}
    />
  );

  if (layout === 'wide') {
    // Render side-by-side directly. No internal modal — the caller is
    // already hosting us in one (the Health verify dialog, etc.). The
    // parent passes `onClose` so the action footer's Close button can
    // dismiss the host modal.
    return (
      <>
        <div className="flex h-full min-h-0 flex-col">{renderSideBySide(onClose)}</div>
        {overridePrompt}
      </>
    );
  }

  return (
    <>
      <div className="space-y-4">
        {renderHeader()}

        {renderMapBody('inline')}

        {renderWarning()}

        {renderReadout()}
        {renderActions()}
      </div>

      <Dialog open={isExpanded} onOpenChange={setIsExpanded}>
        {/*
          Fill ~95% of the viewport on every screen so the map gets the
          space it needs to be useful when reviewers are dragging the
          marker. Cap the absolute width so it doesn't sprawl on
          ultra-wides.

          Use an explicit height (`h-[92vh]`) rather than just `max-h-` so
          the inner flex column has a real container to grow into. With
          `max-h-` alone DialogContent collapses to its content height
          (DialogPrimitive.Content has no intrinsic height), and the
          `h-full` inside the side-by-side grid resolves to 0 — the map
          ends up a tiny strip while the panel fills the rest.

          `p-3` on the body (vs `p-4`) keeps the dialog feeling tight and
          symmetrical — equal spacing on every side, with the card's own
          rounded border supplying the visual breathing room.
        */}
        <DialogContent className="flex h-[92vh] w-[95vw] max-w-[1600px] flex-col p-0 sm:max-w-[95vw]">
          <DialogHeader className="border-b px-6 py-4">
            <DialogTitle>Edit address &amp; location</DialogTitle>
          </DialogHeader>
          <div className="min-h-0 flex-1 p-3">
            {renderSideBySide(() => setIsExpanded(false))}
          </div>
        </DialogContent>
      </Dialog>

      {overridePrompt}
    </>
  );
}
